import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { DialogService } from '@sdbn/appclient';
import { DictionaryEntry } from '@sdbn/common';
import { TranslationService } from '@sdbn/common';
import { IdGeneratorService } from 'src/common/services/id-generator.service';
import { ArticleType, ArticleTypeAttribute, ArticleTypeAttributeGroup, ArticleTypeAttributeType, ArticleTypeAttributeValue } from '../../article-type.model';

@Component({
  selector: 'app-article-type-attribute-editor',
  templateUrl: './article-type-attribute-editor.component.html',
  styleUrls: ['./article-type-attribute-editor.component.scss']
})
export class ArticleTypeAttributeEditorComponent implements OnInit {

  confirmDisabled = false;

  formGroup = new FormGroup({});
  valueFormGroup = new FormGroup({});

  articleType: ArticleType;

  attributeTypes = Object.values(ArticleTypeAttributeType);

  selectedValue: ArticleTypeAttributeValue;

  attribute: ArticleTypeAttribute;
  originalAttribute: ArticleTypeAttribute;

  selectedGroup: ArticleTypeAttributeGroup;
  initialGroup: ArticleTypeAttributeGroup;

  lookupType = false;
  newAttribute = false;

  @ViewChild('lookupValueForm', {static: false})
  lookupValueForm: ElementRef;

  @ViewChild('lookupValueInput', {static: false})
  lookupValueInput: ElementRef;

  constructor(
    public translationService: TranslationService,
    public dialogRef: MatDialogRef<ArticleTypeAttributeEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogService: DialogService,
    private idGenerator: IdGeneratorService,
    private changeDetectorRef: ChangeDetectorRef
  ) {

    this.articleType = data.articleType;
    if (data.attribute != null) {
      this.originalAttribute = data.attribute;
      this.attribute = new ArticleTypeAttribute(data.attribute);
      this.initialGroup = data.group;
      this.newAttribute = false;
    } else {
      this.attribute = new ArticleTypeAttribute({ id: this.idGenerator.getNextId() });
      this.newAttribute = true;
    }

    this.lookupType = this.attribute != null && this.attribute.type === ArticleTypeAttributeType.LOOKUP;

    this.formGroup.addControl('id', new FormControl(this.attribute.id, [Validators.required]));
    this.formGroup.controls.id.disable();
    this.formGroup.addControl('group', new FormControl(null, [Validators.required]));
    this.formGroup.addControl('sortValue', new FormControl('', []));
    this.formGroup.addControl('type', new FormControl(null, [Validators.required]));
    this.formGroup.addControl('name', new FormControl(null, [Validators.required]));
    this.formGroup.addControl('mandatory', new FormControl(false, []));
    this.formGroup.addControl('defaultValue', new FormControl(null, []));
    // form elements only for lookup values
    this.valueFormGroup.addControl('value', new FormControl(null, []));
    this.valueFormGroup.addControl('sortValue', new FormControl(null, []));
    this.valueFormGroup.addControl('default', new FormControl(false, []));


    this.reorderValues();

    this.formGroup.patchValue({
      type: this.attribute.type,
      name: this.attribute.name,
      group: this.initialGroup,
      mandatory: this.attribute.mandatory,
      defaultValue: this.attribute.defaultValue,
      sortValue: this.attribute.sortValue
    });
    console.log(this.formGroup);


    this.formGroup.valueChanges.subscribe(value => {
      this.lookupType = value.type === 'LOOKUP';
      this.selectedGroup = this.findAttributeGroup(value.group);
      this.attribute.mandatory = value.mandatory;
      this.attribute.type = value.type;
      this.attribute.defaultValue = value.defaultValue;
      this.attribute.sortValue = value.sortValue;
    });

    this.formGroup.statusChanges.subscribe(newStatus => {
      this.confirmDisabled = !this.checkArticleTypeValid();
    });


    this.valueFormGroup.valueChanges.subscribe(
      value => {

        this.selectedValue.value = value.value;
        this.selectedValue.sortValue = value.sortValue;
        if (value.default) {
          this.attribute.defaultValueId = this.selectedValue.id;
        }
      }
    );
  }

  private findAttributeGroup(group: ArticleTypeAttributeGroup): ArticleTypeAttributeGroup {
    for (const current of this.articleType?.attributeGroups) {
      if (group.id === current.id) {
        return current;
      }
    }
    return null;
  }

  ngOnInit(): void {

  }

  onClose() {
    this.dialogRef.close();
  }

  onConfirm() {
    if (this.newAttribute) {
      if (this.selectedGroup != null) {
        this.selectedGroup.attributes.push(this.attribute);
      }

    } else { // merge changes back
      this.originalAttribute.defaultValue = this.attribute.defaultValue;
      this.originalAttribute.defaultValueId = this.attribute.defaultValueId;
      this.originalAttribute.deleted = this.attribute.deleted;
      this.originalAttribute.mandatory = this.attribute.mandatory;
      this.originalAttribute.name = this.attribute.name;
      this.originalAttribute.type = this.attribute.type;
      this.originalAttribute.values = this.attribute.values;
      this.originalAttribute.sortValue = this.attribute.sortValue;
    }
    this.dialogRef.close(true);
  }

  public onTitleChanged(dictionary: DictionaryEntry) {
    this.attribute.name = new DictionaryEntry(dictionary);
    this.confirmDisabled = !this.checkArticleTypeValid();
  }

  public selectValue(value: ArticleTypeAttributeValue) {
    this.selectedValue = value;
    this.valueFormGroup.patchValue({
      value: this.selectedValue.value,
      sortValue: this.selectedValue.sortValue,
      default: this.selectedValue.id === this.attribute.defaultValueId
    });
    // Force change detection to ensure input field is available before requesting focus()
    this.changeDetectorRef.detectChanges();
    if (this.lookupValueInput != null) {
      this.lookupValueInput.nativeElement.focus();
    }
  }

  removeValue(value: ArticleTypeAttributeValue) {
    this.dialogService
      .showConfirm('erp.ui.articletype-attribute-editor.dialog.remove-lookupvalue.title', 'erp.ui.articletype-attribute-editor.dialog.remove-lookupvalue.message')
      .afterClosed().subscribe(confirmed => {
        if (confirmed) {
          value.deleted = true;
        }
    });
  }

  public addValue() {
    const newValue = new ArticleTypeAttributeValue({ id: this.idGenerator.getNextId() });
    this.attribute.values.push(newValue);
    this.selectValue(newValue);
  }

  private checkArticleTypeValid(): boolean {
    return this.formGroup.valid && this.attribute.name.checkValueExistsForLanguage('de');
  }

  private reorderValues() {
      this.attribute.values.sort((a,b) => {
        if ( a === null || a.sortValue === null || a.sortValue.length === 0) {
          return -1;
        }
        if ( b === null || b.sortValue === null || b.sortValue.length === 0) {
          return 1;
        }
        return a.sortValue?.localeCompare(b.sortValue);
      });
  }
}
